// Retention is stored as whole days. Years and months are entry helpers only, converted with
// these averages (team decision, design doc 0007) and always rounded up to the next whole day.
// 365.24 days per year and 30.44 days per month, as whole hundredths of a day so they add up
// exactly, without float drift
const HUNDREDTHS_PER_YEAR = 36524;
const HUNDREDTHS_PER_MONTH = 3044;

export type RetentionPeriod = { years: string; months: string; days: string };

const isWholeNumber = (value: string) => /^\d+$/.test(value.trim());

// Returns NaN when every field is empty or any field isn't a whole number, so save-time
// validation rejects it instead of storing a rounded guess (clearing retention isn't supported)
export function periodToDays({ years, months, days }: RetentionPeriod) {
  const fields = [years, months, days];
  if (fields.every((field) => field.trim() === '')) return NaN;
  if (!fields.every((field) => field.trim() === '' || isWholeNumber(field))) return NaN;
  const [y, m, d] = fields.map((field) => Number(field || 0));
  return Math.ceil((y * HUNDREDTHS_PER_YEAR + m * HUNDREDTHS_PER_MONTH + d * 100) / 100);
}

// Splits stored days back into years, months and days. Days are rounded down, which makes
// periodToDays(daysToPeriod(n)) === n for every whole n.
export function daysToPeriod(totalDays: number): RetentionPeriod {
  const total = totalDays * 100;
  const years = Math.floor(total / HUNDREDTHS_PER_YEAR);
  const afterYears = total - years * HUNDREDTHS_PER_YEAR;
  const months = Math.floor(afterYears / HUNDREDTHS_PER_MONTH);
  const days = Math.floor((afterYears - months * HUNDREDTHS_PER_MONTH) / 100);
  return { years: String(years), months: String(months), days: String(days) };
}

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

export function formatRetention(totalDays: number) {
  const { years, months, days } = daysToPeriod(totalDays);
  const parts = [
    [Number(years), 'year'],
    [Number(months), 'month'],
    [Number(days), 'day'],
  ] as const;
  const label = parts
    .filter(([count]) => count > 0)
    .map(([count, unit]) => plural(count, unit))
    .join(', ');
  return label === plural(totalDays, 'day') ? label : `${label} (${plural(totalDays, 'day')})`;
}
